import {
  CustomImage,
  GenericProgressStatus,
  menuEditionStatusFactory,
  Palette,
  ProductEdited,
  ProductMenuEditionStatus,
  ProductType,
  SSOLink,
  ToolsDocument,
} from '../models';

export class ScormModel {
  imsmanifest: ScormManifest;
  imsmanifestRealPath: string;
  webserviceDomaine: string;
  context?: ScormContext;
  editionInfo?: ScormEditionInfo;
  document?: ToolsDocument;
  graphicalParameters?: ScormGraphicalParameters;

  constructor(model?: ScormModel) {
    this.imsmanifest = new ScormManifest(model?.imsmanifest);
    this.editionInfo = new ScormEditionInfo(model?.editionInfo);
    this.imsmanifestRealPath = model?.imsmanifestRealPath;
    this.webserviceDomaine = model?.webserviceDomaine;
    this.context = new ScormContext(model?.context);
    this.graphicalParameters = new ScormGraphicalParameters(model?.graphicalParameters);
  }
}

export class ScormManifest {
  metadata: ScormMetadata;
  resources: ScormResources;
  ['@attributes']: any;
  organizations: ScormOrganizations;

  constructor(manifest?: ScormManifest) {
    this.metadata = new ScormMetadata(manifest?.metadata);
    this.resources = manifest?.resources;
    this.organizations = manifest?.organizations;
    this['@attributes'] = manifest?.['@attributes'];
  }
}

export class ScormMetadata {
  schemaversion: string;
  lom: {
    general: {
      language: string;
      title?: {
        langstring: string;
      };
      description?: {
        langstring: string;
      };
      keyword?: {
        langstring: string;
      };
    };
  };

  constructor(metadata?: ScormMetadata) {
    // De trop nombreuses propriétées non utilisées par le front/back, appartenant au ZIP du Scorm
    Object.assign(this, metadata);
    this.schemaversion = metadata?.schemaversion;
    this.lom = metadata?.lom;
  }
}

export class ScormContext {
  title: string;
  description: string;
  duration: string;

  constructor(context: ScormContext) {
    this.title = context?.title;
    this.description = context?.description;
    this.duration = context?.duration;
  }
}

export class ScormGraphicalParameters {
  backgroundImage: CustomImage;
  palette: Palette;

  constructor(params?: ScormGraphicalParameters) {
    this.backgroundImage = params ? new CustomImage(params?.backgroundImage) : null;
    this.palette = params ? new Palette(params?.palette) : null;
  }
}

export class ScormResources {
  resource: ScormResource[];
  ['@attributes']: any;
}

export class ScormResource {
  file: ScormFile[];
  ['@attributes']: {
    href: string;
    type: string;
    identifier: string;
  };
}

export class ScormFile {
  ['@attributes']: {
    href: string;
  };
}

export class ScormOrganizations {
  ['@attributes']: any;
  organization: ScormOrganization[];
}

export class ScormOrganization {
  ['@attributes']: any;
  item: ScormItem[];
  title: any;
}

export class ScormItem {
  'title': any;
  'metadata': any;
  ['@attributes']: {
    isvisible: string;
    identifier: string;
    identifierref: string;
  };
}

export class ScormStats {
  numberUsers: number;
  numberComplete: number;
  numberPercentage: number;

  constructor(stats?: ScormStats) {
    this.numberUsers = stats?.numberUsers || null;
    this.numberComplete = stats?.numberComplete || null;
    this.numberPercentage = stats?.numberPercentage || null;
  }
}

export class ScormEditionInfo {
  createStatus: ProductMenuEditionStatus;
  paramsStatus: ProductMenuEditionStatus; // Ex metadata
  publicationStatus: ProductMenuEditionStatus;
  ssoLinks: SSOLink[];

  constructor(info?: ScormEditionInfo) {
    const castMetadataToParameterStatus = info?.['metadataStatus'] ?? info?.paramsStatus;
    this.createStatus = menuEditionStatusFactory(info?.createStatus ?? ProductMenuEditionStatus.PRISTINE);
    this.paramsStatus = menuEditionStatusFactory(castMetadataToParameterStatus ?? ProductMenuEditionStatus.PRISTINE);
    this.publicationStatus = menuEditionStatusFactory(info?.publicationStatus ?? ProductMenuEditionStatus.PRISTINE);
    this.ssoLinks = (info?.ssoLinks || []).map(sl => new SSOLink(sl));
  }
}

export class ScormEdited implements ProductEdited<ScormModel> {
  slug: string;
  name: string;
  data: ScormModel;
  productType?: ProductType;
  updatedAt?: Date;
  publishedAt?: Date;
  visible?: boolean;
  stats?: ScormStats;
  shareInProgress?: boolean;
  folderProduct?: { slug: string };
  hasConfigDraft?: boolean;

  constructor(edited?: ScormEdited) {
    this.slug = edited?.slug;
    this.name = edited?.name;
    this.data = edited?.data ? new ScormModel(edited.data) : new ScormModel();
    this.productType = ProductType.SCORM;
    this.visible = edited?.visible;
    this.stats = edited?.stats;
    this.updatedAt = edited?.updatedAt ? new Date(edited.updatedAt) : null;
    this.publishedAt = edited?.publishedAt ? new Date(edited.publishedAt) : null;
    this.shareInProgress = edited?.shareInProgress;
    this.folderProduct = edited?.folderProduct;
    this.hasConfigDraft = edited?.hasConfigDraft;
  }
}

export class ScormProgress {
  scormProperties: ScormProperty[];
  status: GenericProgressStatus;
  time: number;
  score: number;
  percentage?: number;

  constructor(rawProgress?: ScormProgress) {
    this.scormProperties = (rawProgress?.scormProperties || []).map(p => new ScormProperty(p));
    this.status = rawProgress?.status ?? GenericProgressStatus.NOT_STARTED;
    this.time = rawProgress?.time ?? 0;
    this.score = rawProgress?.score;
    this.percentage = rawProgress?.percentage ?? 0;
  }
}

export class ScormProperty {
  key: string;
  value: string;

  constructor(rawProperty?: ScormProperty) {
    this.key = rawProperty?.key;
    this.value = rawProperty?.value;
  }
}
